import { put, takeEvery, select } from 'redux-saga/effects';
import * as Selectors from '../reducers/selectors';
import { v4 as uuidv4 } from 'uuid';
import { addPageItems, removePageItems } from './pageItems';
import { handleCreateThumbnail } from './changesets';
import { renderCanvasFromStore, setSidePanelOpened } from './navigation';
import { ActionTypes, ActionCreators } from 'redux-undo';
import logEvent from '../utils/TagManager';
import { SET_PROJECT, SET_PROJECT_UNDOABLE, updateWarnings } from './project';
import { fromJS, List } from 'immutable';
import * as Layouts from './layouts';
import { REMOVE_PAGE_ITEMS } from './pageItems';
import { SUPPORTS_LAYOUTS } from './layouts';
import { isMobile } from '../components/PicabooResponsive';
import { PAGE_ITEM_SELECTION } from '../actions';

export const CLEAR_SELECTION = 'selection/CLEAR_SELECTION';
export const PAGE_SELECTION = 'selection/PAGE_SELECTION';
const TEXT_SELECTION = 'selection/TEXT_SELECTION';
const TEXT_SELECTED = 'selection/TEXT_SELECTED';
const EYEDROPPER_ENABLE = 'selection/EYEDROPPER_ENABLE';
const SHAPESPLACER_ENABLE = 'selection/SHAPESPLACER_ENABLE';
const BACKGROUND_PLACER_ENABLE = 'selection/BACKGROUND_PLACER_ENABLE';
const LAYOUT_PLACER_ENABLE = 'selection/LAYOUT_PLACER_ENABLE';
const DESIGN_PLACER_ENABLE = 'selection/DESIGN_PLACER_ENABLE';
const PLACER_ENABLE = 'selection/PLACER_ENABLE';
const SWAP_PHOTO_ENABLE = 'selection/SWAP_PHOTO_ENABLE';
export const EYEDROPPER_COLOR = 'selection/EYEDROPPER_COLOR';
const SHAPEPLACER = 'selection/SHAPEPLACER';
const SHAPEADD = 'selection/SHAPEADD';
const COPY_TO_CLIPBOARD = 'selection/COPY_TO_CLIPBOARD'; //triggers saga
const CLEAR_CLIPBOARD = 'selection/CLEAR_CLIPBOARD';
const COPY = 'selection/COPY'; //triggers reducer
const CUT_TO_CLIPBOARD = 'selection/CUT_TO_CLIPBOARD';
const PASTE_FROM_CLIPBOARD = 'selection/PASTE_FROM_CLIPBOARD';
const DELETE_SELECTION = 'selection/DELETE_SELECTION';
const CAPTION_EDITING = 'selection/CAPTION_EDITING';
const SHOW_POSITION_MODAL = 'selection/SHOW_POSITION_MODAL';
const SPELLING_WIZARD_INDEX = 'selection/SPELLING_WIZARD_INDEX';
const SPELLING_WIZARD = 'selection/SPELLING_WIZARD';
const SPELLING_WIZARD_DICTIONARY = 'selection/SPELLING_WIZARD_DICTIONARY';
const DRAGGED_IMAGE_TYPE = 'selection/DRAGGED_IMAGE_TYPE';
const SECTION_SELECTION = 'selection/SECTION_SELECTION';
const SET_SELECTED_SKU = 'selection/SET_SELECTED_SKU';
const ENABLE_EYE_DROPPER_TEXT = 'selection/ENABLE_EYE_DROPPER_TEXT';
const SAVE_DROPPER_TEXT_SELECTED = 'selection/SAVE_DROPPER_TEXT_SELECTED';
const SET_APPLY_LAYOUT_CALLBACK = 'selection/SET_APPLY_LAYOUT_CALLBACK';
const SET_INSERT_AFTER_SPREAD_ID = 'selection/SET_INSERT_AFTER_SPREAD_ID';

export const SELECTED_PAGE_BY_OTHER = 'selection/SELECTED_PAGE_BY_OTHER';
export const SELECTED_PAGE_ITEMS_BY_OTHER =
  'selection/SELECTED_PAGE_ITEMS_BY_OTHER';

const initialState = {
  pageItems: [],
  pages: [],
  textSelection: {},
  dropperTextSelection: { startIndex: -1, endIndex: -1 },
  selectedPagesByOthers: {},
  selectedPagesItemsByOthers: {},
  eyeDropperEnabled: false,
  eyeDropperColor: '#FFFFFF',
  swapPhotoEnabled: false,
  shapeSpotterEnabled: false,
  backgroundPlacerEnabled: false,
  backgroundImage: null,
  layoutPlacerEnabled: false,
  designPlacerEnabled: false,
  captionEditing: false,
  shapePlacerX: 0.33,
  shapePlacerY: 0.33,
  shapeName: 'rect',
  draggedImageType: '',
  clipboardPageItems: [], //Contains the actual items, not just ids
  section: null,
  spellingIndex: null,
  spellingErrors: [],
  spellingState: false,
  selectedSKU: null,
  showPositionModal: false,
  selectedStyles: [],
  thumbnailQueue: [],
  placerEnabled: false,
  placerType: null,
  applyLayoutCallback: null,
  insertAfterSpreadID: '',
};

/**
 *  reducers
 */
const reducer = function reducer(state = initialState, action = {}) {
  let newState;
  switch (action.type) {
    case CLEAR_SELECTION:
      return {
        ...state,
        pageItems: [],
        pages: [],
        textSelection: [],
        selectedStyles: [],
        coordinates: null,
      };
    case SECTION_SELECTION:
      return { ...state, section: action.sectionId };
    case PAGE_ITEM_SELECTION:
      if (action.pageItems.length != 1) {
        state = { ...state, coordinates: null };
      }
      return { ...state, pageItems: action.pageItems };
    case PAGE_SELECTION:
      if (
        action.pages &&
        Array.isArray(action.pages) &&
        action.pages.length > 0
      ) {
        return { ...state, pages: action.pages };
      }
      return state;
    case TEXT_SELECTION:
      return { ...state, textSelection: action.textSelection };
    case TEXT_SELECTED:
      const ts = { startIndex: action.start, endIndex: action.end };
      return {
        ...state,
        selectedStyles: action.selectedStyles,
        textSelection: ts,
      };
    case SAVE_DROPPER_TEXT_SELECTED:
      const dts = {
        startIndex: action.selection.textSelection.startIndex,
        endIndex: action.selection.textSelection.endIndex,
      };
      return { ...state, dropperTextSelection: dts };
    case EYEDROPPER_ENABLE:
      return {
        ...state,
        eyeDropperEnabled: action.onOff,
        eyeDropperCallback: action.callback,
      };
    case SHAPESPLACER_ENABLE:
      return { ...state, shapeSpotterEnabled: action.onOff };
    case BACKGROUND_PLACER_ENABLE:
      return {
        ...state,
        backgroundPlacerEnabled: action.onOff,
        backgroundImage: action.image,
      };
    case LAYOUT_PLACER_ENABLE:
      return {
        ...state,
        layoutPlacerEnabled: action.onOff,
        layouts: action.layouts,
        layoutCallback: action.callback,
      };
    case DESIGN_PLACER_ENABLE:
      return {
        ...state,
        designPlacerEnabled: action.onOff,
        design: action.design,
      };
    case SWAP_PHOTO_ENABLE:
      return { ...state, swapPhotoEnabled: action.onOff };
    case PLACER_ENABLE:
      return {
        ...state,
        placerEnabled: action.onOff,
        placerType: action.placerType,
      };
    case CAPTION_EDITING:
      return { ...state, captionEditing: action.onOff };
    case SHOW_POSITION_MODAL:
      return { ...state, showPositionModal: action.onOff };
    case SHAPEPLACER:
      return { ...state, shapePlacerX: action.x, shapePlacerY: action.y };
    case SHAPEADD:
      return { ...state, shapeName: action.shapeName };
    case EYEDROPPER_COLOR:
      return { ...state, eyeDropperColor: action.color };
    case SELECTED_PAGE_BY_OTHER:
      newState = { ...state };
      newState.selectedPagesByOthers[action.clientID] = {
        displayName: action.user,
        pages: action.pages,
        colorHex: action.colorHex,
      };
      return newState;
    case SELECTED_PAGE_ITEMS_BY_OTHER:
      newState = { ...state };
      newState.selectedPagesItemsByOthers[action.clientID] = {
        displayName: action.user,
        pageItems: action.pageItems,
        colorHex: action.colorHex,
      };
      return newState;
    case DRAGGED_IMAGE_TYPE:
      return { ...state, draggedImageType: action.imageType };
    case CLEAR_CLIPBOARD:
      return { ...state, clipboardPageItems: [] };
    case COPY:
      let items = [];
      for (let id of state.pageItems) {
        const item = action.pageItemObjects.get(id);
        if (item && !item.has('portraitID')) {
          items.push(item);
        }
      }

      return { ...state, clipboardPageItems: items };
    case ActionTypes.UNDO:
    case REMOVE_PAGE_ITEMS:
      return { ...state, pageItems: [] };
    case Layouts.APPLY_LAYOUT_TO_PAGES:
      if (action.changes.pages.length === 1) {
        return { ...state, pageItems: [], pages: action.changes.pages };
      } else {
        return { ...state, pageItems: [] };
      }
    case SPELLING_WIZARD_INDEX:
      return { ...state, spellingIndex: action.index };
    case SPELLING_WIZARD:
      return { ...state, spellingState: action.state };
    case SPELLING_WIZARD_DICTIONARY:
      return { ...state, spellingErrors: action.spellingErrors };
    case SET_SELECTED_SKU:
      return { ...state, selectedSKU: action.selectedSKU };
    case SET_APPLY_LAYOUT_CALLBACK:
      return { ...state, applyLayoutCallback: action.layoutCallback };
    case SET_INSERT_AFTER_SPREAD_ID:
      return { ...state, insertAfterSpreadID: action.spreadID };
    default:
      return state;
  }
};

/**
 *  actions
 */

// more decorator
export function keepSelection(action) {
  action.keepSelection = true;
  return action;
}

export function setApplyLayoutCallback(layoutCallback) {
  return {
    type: SET_APPLY_LAYOUT_CALLBACK,
    layoutCallback,
  };
}

export function setInsertAfterSpreadID(spreadID) {
  return {
    type: SET_INSERT_AFTER_SPREAD_ID,
    spreadID,
  };
}

export function clearSelection() {
  return { type: CLEAR_SELECTION };
}

export function selectSection(sectionId) {
  return { type: SECTION_SELECTION, sectionId };
}

export function selectPageItems(pageItems) {
  return { type: PAGE_ITEM_SELECTION, pageItems };
}
export function deleteSelectedPageItems() {
  return { type: DELETE_SELECTION };
}

export function selectPages(pages) {
  return { type: PAGE_SELECTION, pages };
}

export function selectText(startIndex, endIndex) {
  const selection =
    startIndex > -1 && endIndex > -1 ? { startIndex, endIndex } : {};
  return { type: TEXT_SELECTION, textSelection: selection };
}

export function textSelected(selectedStyles, start = 0, end = 0) {
  return {
    type: TEXT_SELECTED,
    selectedStyles,
    start,
    end,
  };
}

export function setSelectedSKU(selectedSKU) {
  return { type: SET_SELECTED_SKU, selectedSKU };
}

export function setShapePlacerXY(x, y) {
  return { type: SHAPEPLACER, x, y };
}
export function setShape(shapeName) {
  if (shapeName == 'text' || shapeName == 'richText')
    logEvent('Toolbar', 'Add text', 'Started', 0);
  return { type: SHAPEADD, shapeName };
}
export function enableShapePlacer(onOff) {
  return { type: SHAPESPLACER_ENABLE, onOff };
}
export function enableBackgroundPlacer(onOff, image = null) {
  return { type: BACKGROUND_PLACER_ENABLE, onOff, image };
}
export function enableLayoutPlacer(onOff, layouts, callback) {
  return { type: LAYOUT_PLACER_ENABLE, onOff, layouts, callback };
}
export function enableDesignPlacer(onOff, design) {
  return { type: DESIGN_PLACER_ENABLE, onOff, design };
}
export function enableSwapPhotoPicker(onOff) {
  if (onOff) logEvent('Edit photo', 'Swap photo', 'Started', 0);
  return { type: SWAP_PHOTO_ENABLE, onOff };
}

export function activatePlacer(onOff) {
  return { type: PLACER_ENABLE, onOff, placerType: 'CALENDAR_DAY' };
}

export function selectedPagesByAnotherUser(user, pages) {
  return { type: SELECTED_PAGE_BY_OTHER, user, pages };
}
export function draggedImageType(imageType) {
  return { type: DRAGGED_IMAGE_TYPE, imageType };
}

/**
 * So we can know if we are currently editing the text of a caption.
 * @param onOff
 */
export function captionEditing(onOff) {
  return { type: CAPTION_EDITING, onOff };
}

export function showPositionModal(onOff) {
  return { type: SHOW_POSITION_MODAL, onOff };
}

export function spellingWizardIndex(index) {
  return { type: SPELLING_WIZARD_INDEX, index };
}

export function spellingWizardDictionary(spellingErrors) {
  return { type: SPELLING_WIZARD_DICTIONARY, spellingErrors };
}

export function spellingWizardState(state) {
  return { type: SPELLING_WIZARD, state };
}

/**
 * Looks in the selection and copies those items to the clipboard.
 * @returns {{type: string}}
 */
export function copySelectionToClipboard() {
  return { type: COPY_TO_CLIPBOARD };
}

export function cutSelectionToClipboard() {
  return { type: CUT_TO_CLIPBOARD };
}

export function pasteFromClipboard(page) {
  return { type: PASTE_FROM_CLIPBOARD, page };
}

export function saveEyeDropperColor(color) {
  return { type: EYEDROPPER_COLOR, color };
}

export function enableEyeDropper(onOff, callback) {
  if (onOff) logEvent('Eyedropper', 'Eyedropper button clicked', '', 0);
  return { type: EYEDROPPER_ENABLE, onOff, callback };
}

export function enableEyeDropperText(onOff) {
  return {
    type: ENABLE_EYE_DROPPER_TEXT,
    onOff,
  };
}

function* enableEyeDropperTextSaga(color) {
  const selection = yield select(Selectors.selectionSelector);
  yield put({ type: SAVE_DROPPER_TEXT_SELECTED, selection });
  yield put(enableEyeDropper(true));
}

function* copyItemsSaga(action) {
  const selectedItems = yield select(Selectors.selectedPageItems);
  if (!isMaleable(selectedItems)) {
    yield put({ type: CLEAR_CLIPBOARD });
    return;
  }

  // const pageItemObjects = yield select(Selectors.pageItemsSelector);

  const isDesigner = yield select(Selectors.isDesigner);
  if (isDesigner) {
    const project = yield select(Selectors.projectMetadataSelector);
    const pageItemJSON = JSON.stringify({
      projectRatio: project.get('ratio'),
      pageItems: selectedItems.toJS(),
    });
    window.localStorage.setItem('designerClipboard', pageItemJSON);
  }

  const pageItemObjects = yield select(Selectors.pageItemsSelector);
  yield put({ type: COPY, pageItemObjects });
}

function* cutItemsSaga(action) {
  const selectedItems = yield select(Selectors.selectedPageItems);
  if (!isMaleable(selectedItems)) {
    yield put({ type: CLEAR_CLIPBOARD });
    return;
  }

  const isDesigner = yield select(Selectors.isDesigner);
  if (isDesigner) {
    const project = yield select(Selectors.projectMetadataSelector);
    const pageItemJSON = JSON.stringify({
      projectRatio: project.get('ratio'),
      pageItems: selectedItems.toJS(),
    });
    window.localStorage.setItem('designerClipboard', pageItemJSON);
  }

  //Copy to the clipboard
  const pageItemObjects = yield select(Selectors.pageItemsSelector);
  yield put({ type: COPY, pageItemObjects });

  //Now remove the originals
  const pages = yield select(Selectors.selectedPages);
  let page = pages.first();
  let items = [];
  let selection = yield select(Selectors.selectionSelector);
  for (let item of selection.clipboardPageItems) {
    items.push(item.get('id'));
  }

  //deselect the page items
  selectPageItems([]);

  yield put(removePageItems(page.get('id'), items));

  yield put(renderCanvasFromStore());
}

function isMaleable(selectedItems) {
  return selectedItems.every((i) => {
    const restrictedProps = ['namesItem', 'portraits'];
    return !restrictedProps.some((prop) => {
      return i.has(prop);
    });
  });
}

function* deleteItemsSaga(action) {
  const pages = yield select(Selectors.selectedPages);
  const selectedPageItems = yield select(Selectors.selectedPageItems);

  let firstPage = pages.first();
  if (!firstPage) {
    return;
  }
  for (let page of pages) {
    let items = [];
    for (let pageItem of selectedPageItems) {
      let itemID = pageItem.get('id');
      items.push(itemID);
    }

    if (items.length === 0) {
      return;
    }

    let project = yield select(Selectors.projectMetadataSelector);
    let newWarnings = project.get('warnings');
    if (SUPPORTS_LAYOUTS.includes(project.get('type'))) {
      let pageItems = yield select(Selectors.pageItemsSelector);

      let lowResWarnings = [];
      let spellWarnings = [];
      for (let itemID of items) {
        let pi = pageItems.get(itemID);
        if (pi && pi.get('itemType') === 'graphicItem') {
          newWarnings.get('lowResolution').map((lowResWarning) => {
            if (lowResWarning.get('pageItemID') !== pi.get('id')) {
              lowResWarnings.push(lowResWarning);
            }
          });
          newWarnings = newWarnings.delete('lowResolution');
          newWarnings = newWarnings.set('lowResolution', List(lowResWarnings));
        }
        if (pi && pi.get('itemType') === 'richText') {
          newWarnings.get('spelling').map((spellWarning) => {
            if (spellWarning.get('pageItemID') !== pi.get('id')) {
              spellWarnings.push(spellWarning);
            }
          });
          newWarnings = newWarnings.delete('spelling');
          newWarnings = newWarnings.set('spelling', List(spellWarnings));
        }
      }
    }

    selectPageItems([]);
    yield put(updateWarnings(newWarnings));
    yield put(removePageItems(page.get('id'), items));
    yield put(renderCanvasFromStore());
    yield handleCreateThumbnail();
  }
}

function* pasteItemsSaga(action) {
  const selection = yield select(Selectors.selectionSelector);
  const pageItems = yield select(Selectors.pageItemsSelector);
  const isDesigner = yield select(Selectors.isDesigner);
  const projectImages = yield select(Selectors.projectImagesSelector);
  const projectImageIDs = projectImages.keySeq();
  const project = yield select(Selectors.projectMetadataSelector);
  let projectRatio = project.get('ratio');
  let sourceRatio = project.get('ratio');

  if (!action.page) {
    console.warn('No page selected');
    return;
  }

  const pageWidth = 1.0;
  const pageHeight = action.page.get('ratio');
  let DELTA = 0.05;

  let newItems = [];

  let clipboardItems = selection.clipboardPageItems;
  if (isDesigner) {
    DELTA = 0;
    let localClipboard = window.localStorage.getItem('designerClipboard');
    if (localClipboard) {
      let parsedClipBoard = JSON.parse(localClipboard);
      let parsedItems = parsedClipBoard.pageItems;
      sourceRatio = parsedClipBoard.projectRatio;
      if (parsedItems) {
        clipboardItems = fromJS(parsedItems).toArray();
      }
    }
  }

  if (clipboardItems.length == 0) {
    return;
  }

  for (let item of clipboardItems) {
    let newItem = item.set('id', uuidv4().toUpperCase());
    newItem = newItem.set('pageID', action.page.get('id'));

    if (!projectImageIDs.includes(newItem.get('imageID'))) {
      newItem = newItem.set('imageID', null);
    }
    if (isDesigner) {
      if (projectRatio != sourceRatio) {
        let ratioDiff = projectRatio - sourceRatio;
        let posY =
          parseFloat(newItem.get('positionY')) +
          parseFloat(newItem.get('positionY')) * ratioDiff;
        let height =
          parseFloat(newItem.get('height')) +
          (parseFloat(newItem.get('height')) * ratioDiff) / 2; //not sure why i need to divide by 2 here. Math?
        newItem = newItem.set('positionY', posY);
        newItem = newItem.set('height', height);
      }
    } else {
      //Position the item on the page, without covering up the previously existing item
      let posX = parseFloat(newItem.get('positionX'));
      let posY = parseFloat(newItem.get('positionY'));

      posX += DELTA;
      posY += DELTA;

      for (let i = 0; i < 50; i++) {
        let existing = findPageItemByPosition(
          action.page,
          pageItems,
          posX,
          posY,
        );
        if (existing) {
          posX += DELTA;
          posY += DELTA;

          if (posX >= pageWidth) {
            posX = 0;
          }
          if (posY >= pageHeight) {
            posY = 0;
          }
        } else {
          break;
        }
      }

      newItem = newItem.set('positionX', posX);
      newItem = newItem.set('positionY', posY);
    }
    newItems.push(newItem);
  }

  yield put(addPageItems(action.page.get('id'), newItems));
  yield put(renderCanvasFromStore());
}

function* sectionSaga(action) {
  //TODO - when multiple sections come back need to do something else besides getting the first one.
  let sectionId = action.project.currentSection;

  if (sectionId === undefined) {
    for (let p in action.project.sections) {
      sectionId = p;
    }
  }

  if (sectionId) {
    const oldSection = yield select(Selectors.sectionIdSelector);
    if (oldSection !== sectionId) {
      yield put(selectSection(sectionId));
      yield put(ActionCreators.clearHistory());
    }
  }
}

function findPageItemByPosition(page, pageItems, posX, posY) {
  const ids = page.get('pageItems');
  for (let id of ids) {
    const item = pageItems.get(id);
    if (
      item &&
      item.get('positionX') == posX &&
      item.get('positionY') == posY
    ) {
      return item;
    }
  }

  return null;
}

function* mobileSaga() {
  if (isMobile()) {
    yield put(setSidePanelOpened(false));
  }
}

function* setupSagas() {
  yield takeEvery(COPY_TO_CLIPBOARD, copyItemsSaga);
  yield takeEvery(CUT_TO_CLIPBOARD, cutItemsSaga);
  yield takeEvery(PASTE_FROM_CLIPBOARD, pasteItemsSaga);
  yield takeEvery(DELETE_SELECTION, deleteItemsSaga);
  yield takeEvery(SET_PROJECT, sectionSaga);
  yield takeEvery(SET_PROJECT_UNDOABLE, sectionSaga);
  yield takeEvery([LAYOUT_PLACER_ENABLE], mobileSaga);
  yield takeEvery(ENABLE_EYE_DROPPER_TEXT, enableEyeDropperTextSaga);
}

/** ***********************************************************************
 *  Main Export
 *  *********************************************************************** */
export default {
  reducer: reducer,
  saga: setupSagas,
};
